import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close,
  Edit,
  DirectionsCar,
  Info,
  Inventory2,
  LocalShipping,
  Menu as MenuIconMui,
  PhotoLibrary,
  RequestQuote,
} from '@mui/icons-material';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads';
import MediaGalleryDialog, {
  type MediaGalleryItem,
} from 'views/SuperAdmin/Management/TransporterManagement/components/dialogs/MediaGalleryDialog';
import { resolveStoragePathsToUrls } from '../../util/inventoryLeadPhotos';

export interface InventoryLeadDetailDialogProps {
  open: boolean;
  onClose: () => void;
  lead: InventoryLead;
  contactName: string;
  companyName: string;
  onEdit: (lead: InventoryLead) => void;
  columnName?: string;
  columnColor?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  if (value !== index) return null;
  return (
    <div
      role="tabpanel"
      id={`inventory-lead-detail-tabpanel-${index}`}
      aria-labelledby={`inventory-lead-detail-tab-${index}`}
    >
      <Box sx={{ p: { xs: isSmallScreen ? 1.5 : 2, md: 3 } }}>{children}</Box>
    </div>
  );
};

interface TabDef {
  label: string;
  icon: React.ReactElement;
}

const TABS: TabDef[] = [
  { label: 'Overview', icon: <Info fontSize="small" /> },
  { label: 'Trucks', icon: <DirectionsCar fontSize="small" /> },
  { label: 'Shipping', icon: <LocalShipping fontSize="small" /> },
  { label: 'Negotiation', icon: <RequestQuote fontSize="small" /> },
  { label: 'Inventory', icon: <Inventory2 fontSize="small" /> },
];

const InventoryLeadOverviewTab = lazy(() => import('./tabs/InventoryLeadOverviewTab'));
const InventoryLeadTrucksTab = lazy(() => import('./tabs/InventoryLeadTrucksTab'));
const InventoryLeadNegotiationTab = lazy(() => import('./tabs/InventoryLeadNegotiationTab'));
const InventoryLeadShippingTab = lazy(() => import('./tabs/InventoryLeadShippingTab'));
const InventoryLeadInventoryTab = lazy(() => import('./tabs/InventoryLeadInventoryTab'));

const TabFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
);

const getInitials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const InventoryLeadDetailDialog: React.FC<InventoryLeadDetailDialogProps> = ({
  open,
  onClose,
  lead: leadProp,
  contactName,
  companyName,
  onEdit,
  columnName,
  columnColor,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { inventoryLeads } = useInventoryLeads();
  const [tabValue, setTabValue] = useState(0);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [galleryItems, setGalleryItems] = useState<MediaGalleryItem[] | null>(null);

  const lead = useMemo(
    () => inventoryLeads?.find((item) => item.id === leadProp.id) ?? leadProp,
    [inventoryLeads, leadProp],
  );

  const accent = columnColor || theme.palette.primary.main;
  const statusLabel = columnName || lead.status || 'Pending';
  const displayName = contactName?.trim() || companyName?.trim() || 'Inventory lead';
  const trucks = lead.trucks;
  const headerPhotoCount = useMemo(
    () => trucks?.reduce((sum, truck) => sum + (truck.photoStoragePaths?.length ?? 0), 0) ?? 0,
    [trucks],
  );

  useEffect(() => {
    if (!open) {
      setTabValue(0);
      setMobileMenuAnchor(null);
      setGalleryItems(null);
    }
  }, [open, lead.id]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) =>
    setTabValue(newValue);
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
    setMobileMenuAnchor(event.currentTarget);
  const handleMobileMenuClose = () => setMobileMenuAnchor(null);
  const handleMobileMenuSelect = (index: number) => {
    setTabValue(index);
    setMobileMenuAnchor(null);
  };

  const openAllPhotos = async () => {
    if (!trucks?.length) return;
    const paths = trucks.flatMap((truck) => truck.photoStoragePaths ?? []);
    if (!paths.length) return;
    const urls = await resolveStoragePathsToUrls(paths);
    const items: MediaGalleryItem[] = [];
    trucks.forEach((truck, truckIndex) => {
      (truck.photoStoragePaths ?? []).forEach((path, photoIndex) => {
        const title = [truck.year || '', truck.make, truck.model]
          .filter(Boolean)
          .join(' ')
          .trim() || 'Truck';
        items.push({
          key: path,
          storagePath: path,
          prefetchedUrl: urls[path],
          mediaKind: 'photo',
          noteText: title,
          metaText: [
            `Truck ${truckIndex + 1}`,
            `Photo ${photoIndex + 1} of ${truck.photoStoragePaths?.length ?? 0}`,
            truck.vin?.trim() ? `VIN ${truck.vin.trim()}` : '',
          ]
            .filter(Boolean)
            .join(' · '),
        });
      });
    });
    if (items.length) setGalleryItems(items);
  };

  return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
        disableEnforceFocus
        PaperProps={{
        sx: {
          height: { xs: '100vh', md: '90vh' },
          maxHeight: { xs: '100vh', md: '90vh' },
          borderRadius: { xs: 0, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 2, md: 2.5 },
          flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(accent, 0.15)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(accent, 0.25)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              bgcolor: alpha(accent, 0.2),
              color: accent,
              fontWeight: 700,
              width: 44,
              height: 44,
            }}
          >
            {getInitials(displayName)}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.25 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="caption"
                sx={{
                  color: accent,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                }}
              >
                Inventory lead
              </Typography>
              {lead.leadNumber?.trim() ? (
                <Chip
                  size="small"
                  label={lead.leadNumber.trim()}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              ) : null}
              <Chip
                size="small"
                label={statusLabel}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: accent,
                  color: theme.palette.getContrastText(accent),
                  '& .MuiChip-label': { px: 0.85 },
                }}
              />
            </Stack>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ lineHeight: 1.15, color: 'text.primary' }}
              noWrap
            >
              {displayName}
            </Typography>
            {companyName?.trim() && contactName?.trim() ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.3 }}
                noWrap
              >
                {companyName}
              </Typography>
            ) : null}
          </Box>
          {headerPhotoCount > 0 ? (
            <Tooltip title="View all truck photos">
              <IconButton
                onClick={() => void openAllPhotos()}
                size="small"
                sx={{ color: accent, '&:hover': { bgcolor: alpha(accent, 0.12) } }}
              >
                <PhotoLibrary />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Edit inventory lead">
            <IconButton
              onClick={() => onEdit(lead)}
              size="small"
              sx={{
                color: accent,
                '&:hover': { bgcolor: alpha(accent, 0.12) },
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06) },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box
        sx={{
          px: { xs: 1.5, md: 3 },
          pt: 1,
          pb: 0.5,
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {isSmallScreen ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
              {TABS[tabValue]?.icon}
              <Typography variant="subtitle2" fontWeight={700}>
                {TABS[tabValue]?.label}
              </Typography>
            </Stack>
            <IconButton
              edge="end"
              size="small"
              aria-label="menu"
              onClick={handleMobileMenuOpen}
            >
              <MenuIconMui />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
            >
              {TABS.map((tab, idx) => (
                <MenuItem
                  key={tab.label}
                  onClick={() => handleMobileMenuSelect(idx)}
                  selected={tabValue === idx}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {tab.icon}
                    <Typography variant="body2">{tab.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
              },
            }}
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.label}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
              />
            ))}
          </Tabs>
        )}
      </Box>

      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        <TabPanel value={tabValue} index={0}>
          <Suspense fallback={<TabFallback />}>
            <InventoryLeadOverviewTab
              lead={lead}
              contactName={contactName}
              companyName={companyName}
              statusLabel={statusLabel}
              accent={accent}
            />
          </Suspense>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Suspense fallback={<TabFallback />}>
            <InventoryLeadTrucksTab
              lead={lead}
              accent={accent}
              onOpenGallery={setGalleryItems}
              enabled={open && tabValue === 1}
            />
          </Suspense>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Suspense fallback={<TabFallback />}>
            <InventoryLeadShippingTab lead={lead} enabled={open && tabValue === 2} />
          </Suspense>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Suspense fallback={<TabFallback />}>
            <InventoryLeadNegotiationTab
              lead={lead}
              trucks={trucks ?? []}
              enabled={open && tabValue === 3}
            />
          </Suspense>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Suspense fallback={<TabFallback />}>
            <InventoryLeadInventoryTab
              lead={lead}
              contactName={contactName}
              enabled={open && tabValue === 4}
            />
          </Suspense>
        </TabPanel>
      </DialogContent>

      {galleryItems && galleryItems.length > 0 ? (
        <MediaGalleryDialog
          open
          onClose={() => setGalleryItems(null)}
          photos={galleryItems}
          videos={[]}
        />
      ) : null}
    </Dialog>
  );
};

export default InventoryLeadDetailDialog;
